/* eslint-disable check-file/filename-blocklist */
import type React from 'react';
import type { MessageDescriptor, WrappedComponentProps } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import type { ColorAppearance } from '../ContentStateColor.tsx';

import type { ContentStateRestrictionLevel } from './__generated__/EditorContentStateQuery.graphql.ts';
import type { ContentStateStateContainer } from './ContentStateStateContainer.tsx';

export const customGroupLabel = 'custom';
export const suggestedGroupLabel = 'suggested';

export type FloatingToolbarButtonStyleType = {
	height: string;
	alignItems: 'center';
};

export type ContentState = {
	color: string | null;
	name: string | null;
	id?: string | null;
	restrictionLevel?: ContentStateRestrictionLevel | null;
	isCallerPermitted?: boolean | null;
	lastModifierAaid?: string | null;
};

export type ContentStateState = {
	contentState: ContentState | null;
	contentStateLastUpdated: string | null;
	shouldRunContentStateEffect: boolean;
	defaultStatesArray: (ContentState | null)[];
	customStatesArray: (ContentState | null)[];
	isContentStateEnabled: boolean;
	isSuggestedEnabled: boolean;
	isCustomEnabled: boolean;
	isUpdating: boolean;
	lastFetchedIsDraft: boolean | null;
	lastFetchedContentId: string | null;
};

export type EditorContentStateProps = {
	contentId: string;
	contentType: 'page' | 'blogpost' | null;
	contentStateState: ContentStateStateContainer;
	flags: FlagsStateContainer;
	isPublishingContent?: boolean;
	userOffline: boolean;
	forceContentStatePlacementToTop?: boolean;
	onUpdateSuccess?: () => void;
	onContentStateMenuToggle?: (newState: boolean) => void;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	renderPlusButtonEmptyState?: boolean;
	isDetailsPanel?: boolean;
	onClick?: () => void;
	isViewMode?: boolean;
	canEdit?: boolean;
};

export type ContentStateButtonProps = WrappedComponentProps & {
	contentState: ContentState | null;
	activeWorkflowStatus?: WorkflowStatus | null;
	isSelected: boolean;
	isWarning: boolean;
	triggerProps: any;
	togglePopup: () => void;
	userOffline: boolean;
	contentStateButtonRef: React.RefObject<HTMLElement | null>;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	renderPlusButtonEmptyState?: boolean;
	isDetailsPanel?: boolean;
};

export type ContentStateButtonBaseProps = {
	contentState: ContentState | null;
	isSelected: boolean;
	isWarning: boolean;
	triggerProps: any;
	togglePopup: () => void;
	userOffline: boolean;
	contentStateButtonRef: React.RefObject<HTMLElement | null>;
	appearance: string;
	primaryColor: string;
	secondaryColor: string;
	hoverColor: string;
	showChevron: boolean;
	colorAppearance: ColorAppearance;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	tooltipContent: MessageDescriptor;
	isDetailsPanel?: boolean;
};

export type RemoveButtonProps = WrappedComponentProps & {
	hasContentState: boolean;
	onClick: () => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	userOffline: boolean;
};

export type ContentStateMenuProps = WrappedComponentProps & {
	contentId: string;
	contentStateState: ContentStateStateContainer;
	contentType: 'page' | 'blogpost' | null;
	isContentStateEnabled: boolean;
	isCustomEnabled: boolean;
	customStatesArray: (ContentState | null)[];
	defaultStatesArray: (ContentState | null)[];
	isSuggestedEnabled: boolean;
	isCreatingContentState: boolean;
	selectedColor: string | null;
	onSelectColor: (newColor: string) => void;
	isColorPickerOpen: boolean;
	onColorPickerClose: () => void;
	onColorPickerToggle: () => void;
	onPopupClose: () => void;
	onSubmit: (name: string) => void;
	onCancel: () => void;
	onRemove: () => void;
	onFocusTextField: () => void;
	onMouseEnterRemove: () => void;
	onMouseLeaveRemove: () => void;
	userOffline: boolean;
	contentStateButtonRef: React.RefObject<HTMLElement | null>;
	onUpdateSuccess?: () => void;
	isContentStateMenuOpen: boolean;
	setIsContentStateMenuOpen: (newValue: boolean) => void;
	updatePopup: () => void;
};

export type ContentStateFormProps = WrappedComponentProps & {
	onSubmit: (name: string) => void;
	onCancel: () => void;
	showFormFooter: boolean;
	selectedColor: string | null;
	onSelectColor: (newColor: string) => void;
	isColorPickerOpen: boolean;
	onColorPickerClose: () => void;
	onColorPickerToggle: () => void;
	onFocusTextField: () => void;
	userOffline: boolean;
	flags: FlagsStateContainer;
	colorPickerComponentRef: React.RefObject<HTMLElement | null>;
};

export type ContentStateTextFieldProps = WrappedComponentProps & {
	showFormFooter: boolean;
	onFocusTextField: () => void;
	userOffline: boolean;
};

export type ColorPickerButtonProps = {
	selectedColor: string | null;
	onSelectColor: (newColor: string) => void;
	isOpen: boolean;
	onClose: () => void;
	onColorPickerToggle: () => void;
	userOffline: boolean;
	colorPickerComponentRef: React.RefObject<HTMLElement | null>;
};

export type ContentStateGroupProps = WrappedComponentProps & {
	contentId: string;
	contentStateState: ContentStateStateContainer;
	groupIsEnabled: boolean;
	groupLabel: string;
	contentStates: (ContentState | null)[];
	flags: FlagsStateContainer;
	userOffline: boolean;
	onUpdateSuccess?: () => void;
	setIsContentStateMenuOpen: (newValue: boolean) => void;
};

export type ContentStateProps = {
	contentState: ContentState;
	onClick: () => void;
	userOffline: boolean;
};

export type CreateContentStateFormData = {
	name: string;
};
